import { capitalize } from 'lodash';
import { useMemo } from 'react';
import {
  Badge,
  Box,
  Button,
  DataTable,
  DataTableBody,
  Disclosure,
  DisclosureContent,
  DisclosureTrigger,
  Flex,
  Separator,
  Switch,
  Text,
  Tooltip,
} from '@optiaxiom/react';
import { Pill } from '@optiaxiom/react/unstable';
import {
  IconBracketsCurly,
  IconBracketsSquare,
  IconInputNumeric,
  IconInputText,
  IconLocationDot,
  IconScrewdriverWrench,
  IconSlider,
  IconToggleOff,
  IconUserLock,
  IconXmark,
} from '@optimizely/axiom-icons';
import { createColumnHelper, getCoreRowModel, useReactTable } from '@tanstack/react-table';

import { CustomIcon, TruncatedList } from '@frontend/components/ui';
import type { ParameterDetail, Tool } from '@frontend/types';

import * as styles from './ToolRowDetailsSidebar.css';

type ParameterRow = {
  name: string;
  type: string;
  description: string;
  required: boolean;
};

const PARAMETER_ICONS: Record<string, JSX.Element> = {
  string: <IconInputText size="16" />,
  number: <IconInputNumeric size="16" />,
  integer: <IconInputNumeric size="16" />,
  boolean: <IconToggleOff size="16" />,
  array: <IconBracketsSquare size="16" />,
  object: <IconBracketsCurly size="16" />,
};

const columnHelper = createColumnHelper<ParameterRow>();

const columns = [
  columnHelper.accessor('name', {
    cell: ({ row }) => (
      <Flex flexDirection="row" gap="8" alignItems="start">
        <Tooltip
          content={
            row.original.type.toLowerCase() === 'object' ? 'JSON' : capitalize(row.original.type)
          }
        >
          <Box pt="2">
            {PARAMETER_ICONS[row.original.type.toLowerCase()] ?? <IconSlider size="16" />}
          </Box>
        </Tooltip>
        <Flex flexDirection="column" gap="2">
          <Flex flexDirection="row" gap="2">
            <Text fontSize="sm" fontWeight="600">
              {row.original.name}
            </Text>
            {row.original.required && (
              <Text fontSize="sm" fontWeight="600" color="fg.error">
                *
              </Text>
            )}
          </Flex>
          <Tooltip content={row.original.description} auto>
            <Text fontSize="sm" color="fg.secondary" className={styles.toolDescription}>
              {row.original.description}
            </Text>
          </Tooltip>
        </Flex>
      </Flex>
    ),
    header: 'Parameter',
  }),
];

function ParametersDisclosure({ parameters }: { parameters: Record<string, ParameterDetail> }) {
  const data = useMemo(
    () =>
      Object.entries(parameters).map(([name, param]) => ({
        name,
        type: param.type,
        description: param.description,
        required: param.required,
      })),
    [parameters]
  );

  const hasRequiredFields = useMemo(() => data.some(param => param.required), [data]);

  const table = useReactTable({
    columns,
    data,
    getCoreRowModel: getCoreRowModel(),
  });

  if (data.length === 0) {
    return null;
  }

  return (
    <Disclosure w="full">
      <DisclosureTrigger chevronPosition="end" alignItems="center" py="0">
        <Flex flexDirection="row" gap="8" alignItems="center">
          <IconSlider />
          <TruncatedList lineClamp={1} gap="6">
            {data.map(param => (
              <Pill key={param.name}>{param.name}</Pill>
            ))}
          </TruncatedList>
        </Flex>
      </DisclosureTrigger>
      <DisclosureContent>
        <Flex flexDirection="column" gap="8" pt="8">
          <DataTable table={table}>
            <DataTableBody />
          </DataTable>
          {hasRequiredFields && (
            <Flex flexDirection="row" gap="2">
              <Text fontSize="xs" color="fg.error">
                *
              </Text>
              <Text fontSize="xs" color="fg.tertiary">
                Required fields
              </Text>
            </Flex>
          )}
        </Flex>
      </DisclosureContent>
    </Disclosure>
  );
}

export function ToolRowDetailsSidebar({
  selectedTool,
  onClose,
  onEnabledInChatChange,
  onToggleActive,
}: {
  selectedTool: Tool;
  onClose: () => void;
  onEnabledInChatChange: (enabled: boolean) => void;
  onToggleActive: (toolId: string, toolName: string, isDisabled: boolean) => void;
}) {
  return (
    <Flex flexDirection="column" gap="16" p="16" h="full" justifyContent="space-between">
      <Flex
        flexDirection="column"
        gap="16"
        overflow="auto"
        flex="1"
        className={styles.scrollableContent}
      >
        <Flex flexDirection="column" gap="8">
          <Flex flexDirection="row" justifyContent="space-between" alignItems="center">
            <Flex flexDirection="row" alignItems="center" gap="8">
              <Box
                className={styles.iconContainer}
                display="flex"
                alignItems="center"
                justifyContent="center"
              >
                <IconScrewdriverWrench size="20" />
              </Box>
              <Text fontWeight="600" fontSize="lg" className={styles.toolName}>
                {selectedTool.name}
              </Text>
              {selectedTool.is_system_function && (
                <Tooltip content="System tool">
                  <Box>
                    <CustomIcon name="optimizely" alt="Opal System Tool" height={12} />
                  </Box>
                </Tooltip>
              )}
              {selectedTool.is_preview && <Badge intent="information">Preview</Badge>}
            </Flex>
            <Flex flexDirection="row" gap="8" alignItems="center">
              <Button
                icon={<IconXmark size="16" />}
                appearance="subtle"
                onClick={onClose}
                aria-label="Close sidebar"
              />
            </Flex>
          </Flex>
          {selectedTool.is_disabled === false && (
            <Flex w="fit">
              <Badge intent={'success'}>Activated</Badge>
            </Flex>
          )}
          <Text fontSize="md">{selectedTool.description}</Text>
        </Flex>
        <Flex flexDirection="row" gap="8" alignItems="center">
          <IconLocationDot />
          <Flex flexDirection="row" gap="6" flexWrap="wrap">
            <Pill>{selectedTool.service.name}</Pill>
          </Flex>
        </Flex>
        {(selectedTool.is_system_function || selectedTool.auth_requirements.length > 0) && (
          <Flex flexDirection="row" gap="8" alignItems="center">
            <IconUserLock />
            <Flex flexDirection="row" gap="6" flexWrap="wrap">
              {selectedTool.is_system_function ? (
                <Pill>Opti ID</Pill>
              ) : (
                selectedTool.auth_requirements.map(req => (
                  <Pill key={req.provider}>{req.provider}</Pill>
                ))
              )}
            </Flex>
          </Flex>
        )}
        <ParametersDisclosure parameters={selectedTool.parameters} />
        <Separator />
        <Switch
          checked={selectedTool.is_enabled_in_primary_agent}
          onCheckedChange={onEnabledInChatChange}
        >
          Enabled in chat
        </Switch>
      </Flex>
      <Flex justifyContent="end" gap="8" flexDirection="row">
        <Button
          disabled={selectedTool.is_system_function}
          onClick={() =>
            onToggleActive(selectedTool.function_id, selectedTool.name, selectedTool.is_disabled)
          }
        >
          {selectedTool.is_disabled ? 'Activate' : 'Deactivate'}
        </Button>
        <Button onClick={onClose}>Close</Button>
      </Flex>
    </Flex>
  );
}
